// livestreamChat.js
'use strict';
import { fromJS } from 'immutable';
import {
  PUSH_LIVESTREAM_CHAT_MESSAGES,
  MERGE_LIVESTREAM_CHAT_DATA,
  SET_LIVESTREAM_CHAT_DATA,
  // -- PLOP_PREPEND_REDUCER_ACTION_TYPE --
  LOGOUT,
} from '../ActionTypes.js';

/*
Chat message bodies, kept out of the `liveStream` slice so message floods
never touch cold UI data. The list windows (itemIds) stay in `lists`.
{
  [streamId]: {
    [chatMessageId]: {
      id: String,
      chatMessageId: String,
      streamId: String,
      senderId: String,
      text: String,
      ...
    }
  }
}
*/
export const defaultState = fromJS({});

/**
 * Live stream chat.
 * @module reducer/livestreamChat
 */
const livestreamChat = (state = defaultState, action) => {
  switch (action.type) {
    case PUSH_LIVESTREAM_CHAT_MESSAGES:
      return _PUSH_LIVESTREAM_CHAT_MESSAGES(action.payload)(state);
    case MERGE_LIVESTREAM_CHAT_DATA:
      return _MERGE_LIVESTREAM_CHAT_DATA(action.payload)(state);
    case SET_LIVESTREAM_CHAT_DATA:
      return _SET_LIVESTREAM_CHAT_DATA(action.payload)(state);
    // -- PLOP_PREPEND_REDUCER_SWITCH_CASE --
    case LOGOUT:
      return _LOGOUT(action.payload)(state);
    default:
      return state;
  }
};

/**
 * Push live stream chat messages: merge the new message bodies and, in the
 * same action, delete the bodies whose ids were trimmed out of the chat list
 * window (lists reducer handles the itemIds side of this action).
 * @kind reducer/actionType
 * @name PUSH_LIVESTREAM_CHAT_MESSAGES
 * @param {string} {streamId} - stream id
 * @param {object} {messagesById} - new chat messages keyed by chat message id
 * @param {array} {removedItemIds} - chat message ids trimmed out of the window
 * @return {Immutable.Map} New state
 */
const _PUSH_LIVESTREAM_CHAT_MESSAGES =
  ({ streamId, messagesById, removedItemIds = [] }) =>
  state =>
    removedItemIds.reduce(
      (currentState, chatMessageId) =>
        currentState.deleteIn([streamId, chatMessageId]),
      state.mergeDeepIn([streamId], messagesById)
    );

/**
 * Merge live stream chat data
 * @kind reducer/actionType
 * @name MERGE_LIVESTREAM_CHAT_DATA
 * @param {array} {selectPath} - select path
 * @param {any} {data} - chat data
 * @return {Immutable.Map} New state
 */
const _MERGE_LIVESTREAM_CHAT_DATA =
  ({ selectPath, data }) =>
  state => {
    return state.mergeDeepIn(selectPath, data);
  };

/**
 * Set live stream chat data
 * @kind reducer/actionType
 * @name SET_LIVESTREAM_CHAT_DATA
 * @param {array} {selectPath} - select path
 * @param {any} {data} - chat data
 * @return {Immutable.Map} New state
 */
const _SET_LIVESTREAM_CHAT_DATA =
  ({ selectPath, data }) =>
  state => {
    return state.setIn([...selectPath], fromJS(data));
  };

// -- PLOP_PREPEND_REDUCER_ACTION_HANDLER --

/**
 * Logout
 * @kind reducer/actionType
 * @name LOGOUT
 * @return {Immutable.Map} New state
 */
const _LOGOUT = () => () => {
  return defaultState;
};

export default livestreamChat;
